// The armed pick (docs/claude/tools.md, *An armed pick*): the ONE "the next
// click means X" — a polar array's centre, a ceiling's wall, the object whose
// colours to copy, a product to place, a connection bend picked up, the polygons
// a catalogue part is drawn as, a relation waiting for its second segment. Every
// handler asks this slot once, first: the canvas press (`downArmedPick`), the
// pointer (`moveArmedPick`), Esc / right-click / setTool / undo / entering 3D
// (`cancelArmed`), a selection change (`settleArmed`), the bar's prompt and the
// cursor. A pick says what it does; arming one puts down the one before.
import type { Vec2 } from '../../shared/types';
import {
  type Armed,
  armedSlot,
} from '../armedSlot';
import {
  invalidate,
  renderProperties,
  state,
  updateStatus,
} from './core';
import {
  type BenchHover,
  canvas,
} from '../main';

/** Which pick is armed (a panel lights its button by it). */
export type ArmedKind =
  | 'array-centre'
  | 'ceiling-wall'
  | 'copy-colours'
  | 'part-polygons'
  | 'place-product'
  | 'relation'
  | 'waypoint';

/** An armed pick: what it does with the next click, and how it lets go. A pick
 *  with no `click` rides on Select's own press (a relation applies when its
 *  segment is selected; a part's polygons are a marquee). */
export interface ArmedPick extends Armed {
  readonly kind: ArmedKind;
  /** The bar's prompt while it is armed (translated). */
  prompt?(): string;
  /** The canvas cursor while it is armed. */
  readonly cursor?: string;
  /** Its click works on a sheet too; otherwise a sheet's press is the sheet's. */
  readonly onSheets?: boolean;
  /** The next left click (screen px): 'keep' leaves it armed (a click that
   *  missed); otherwise it is over. */
  click?(e: MouseEvent, sp: Vec2): 'keep' | void;
  /** The pointer moving while it is armed; true when it took the move (nothing
   *  else hovers). */
  move?(e: MouseEvent, sp: Vec2): boolean;
  /** What its click here would take, lit (world mm). */
  lit?(world: Vec2): BenchHover | null;
  /** Its left click in the 3D view (client px): 'keep' leaves it armed. A pick
   *  with none lets the 3D view's click be the view's. */
  click3d?(clientX: number, clientY: number): 'keep' | void;
  /** The pointer moving over the 3D view while it is armed (client px). */
  move3d?(clientX: number, clientY: number): void;
  /** The 3D view's cursor while it is armed (pointer3d.ts: over a handle or
   *  the gizmo too — its click takes them). */
  readonly cursor3d?: string;
}

/** A settle runs from inside the panel's own render, which is under way. */
let settling = false;
const slot = armedSlot<ArmedPick>((now, before) => {
  if (now?.cursor) canvas.style.cursor = now.cursor;
  else if (before?.cursor && canvas.style.cursor === before.cursor) canvas.style.cursor = '';
  if (before?.lit && state.benchAddHover?.kind === 'pick') state.benchAddHover = null; // what it lit goes with it
  updateStatus();
  if (!settling) renderProperties();
  invalidate();
});

/** Arm a pick (the one armed before it is cancelled). */
export function armPick(p: ArmedPick): void { slot.arm(p); }
/** The armed pick, or null. */
export function armed(): ArmedPick | null { return slot.current(); }
/** Whether a pick of this kind is armed. */
export function armedIs(kind: ArmedKind): boolean { return slot.current()?.kind === kind; }
/** Let go of the armed pick, putting back what it changed: Esc, a right-click,
 *  another tool, undo, the 3D view. True when one was armed. */
export function cancelArmed(): boolean { return slot.cancel(); }
/** The armed pick's work is done another way (its marquee, its segment). */
export function endArmed(): ArmedPick | null { return slot.end(); }
/** A selection change or an edit: a pick that no longer fits ends. */
export function settleArmed(): void {
  settling = true;
  try { slot.settle(); } finally { settling = false; }
}

/** The canvas press: the armed pick's click, if it takes one (a left click, on
 *  the plan — or on a sheet, for a pick that works there). */
export function downArmedPick(e: MouseEvent, sp: Vec2, onSheet: boolean): boolean {
  const p = slot.current();
  if (e.button !== 0 || !p?.click || (onSheet && !p.onSheets)) return false;
  return slot.take((q) => q.click!(e, sp));
}

/** The pointer moving: true when the armed pick took the move. */
export function moveArmedPick(e: MouseEvent, sp: Vec2): boolean {
  return slot.current()?.move?.(e, sp) ?? false;
}

/** The bar's prompt for the armed pick, or ''. */
export function armedPrompt(): string { return slot.current()?.prompt?.() ?? ''; }
/** The cursor the armed pick shows, or ''. */
export function armedCursor(): string { return slot.current()?.cursor ?? ''; }
/** Whether the next click is the armed pick's (no Select hover promises it). */
export function armedTakesClick(): boolean { return !!slot.current()?.click; }
